// ── Human Protein Atlas lookup, done client-side ────────────────────
// The production backend's own outbound network can't reach
// proteinatlas.org (confirmed ECONNREFUSED - an ITS firewall
// restriction). The Admin's browser has no such restriction, and HPA's
// API sends Access-Control-Allow-Origin: *, so this does the same
// gene → Ensembl ID lookup generator.js's fetchEnsemblForGene does, but
// from here, and the resolved URL is sent to the backend as
// human_protein_atlas_override so it doesn't need to make the call itself.
//
// Shared by ProductDetails.js (single-item Generate) and BulkActions.js
// (Bulk Generate) - one implementation instead of two copies drifting
// out of sync with each other.
//
// Returns every exact (case-insensitive) gene-symbol match, not just the
// first - if HPA ever has more than one Ensembl ID under the same
// displayed symbol, all of them get a link rather than silently picking
// one, joined with "|" (same separator convention generator.js uses for
// other multi-value fields like Collections).
export async function fetchHpaEnsemblUrls(gene) {
  try {
    const url = `https://www.proteinatlas.org/api/search_download.php?format=tsv&columns=g,ge,eg,up&compress=no&search=${encodeURIComponent(gene)}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const text = await res.text();
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];

    const headers = lines[0].split("\t").map((h) => h.trim().toLowerCase());
    const geneIdx = headers.findIndex((h) => h === "gene");
    const ensemblIdx = headers.findIndex((h) => h.includes("ensembl"));
    if (geneIdx === -1 || ensemblIdx === -1) return [];

    const geneLower = gene.toLowerCase();
    const urls = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split("\t");
      const returnedGene = (cols[geneIdx] || "").trim();
      const ensembl = (cols[ensemblIdx] || "").trim();
      if (returnedGene && ensembl && returnedGene.toLowerCase() === geneLower) {
        urls.push(`https://www.proteinatlas.org/${ensembl}-${gene}`);
      }
    }
    return urls;
  } catch (e) {
    console.warn("HPA lookup failed for gene", gene, ":", e.message);
    return [];
  }
}

// Convenience wrapper for a full deposit object - checks Antigen
// Species/Gene the same way generator.js's getHumanProteinAtlas does,
// and returns the "|"-joined override string ready to attach, or null
// when the deposit doesn't qualify (not human, or no gene) or nothing
// was found.
export async function computeHpaOverride(deposit) {
  const antigenSpecies = (deposit.antigen_species || "").toLowerCase();
  const gene = (deposit.gene || "").trim();
  if (!antigenSpecies.includes("human") || !gene || gene === "NaN") return null;
  const urls = await fetchHpaEnsemblUrls(gene);
  return urls.length ? urls.join("|") : null;
}
